/*!
 * oscillaOSCClient.js — Client-Side OSC output
 * Part of oscillaScore control plane architecture
 * © 2025 Rob Canning — GPLv3
 *
 * Every cue's OSC goes out through sendOSC: throttled per address, rounded,
 * recorded in the live console's OSC monitor, and sent to the server over
 * the WebSocket, which forwards it as OSC. The mute is this client's OSC
 * output switch. OSC input is the server's and controlRouter.js's.
 */

// ===========================
// Module State
// ===========================

/** @type {boolean} This client's OSC output is muted */
let oscMuted = false;

// ===========================
// OSC Monitor State
// ===========================
// Shared Map for the live console OSC monitor section.
// Keyed by address string, each entry holds the latest message state.
// Exposed on window so the live console can read it without importing.

if (!window.oscillaOSCMonitor) {
  window.oscillaOSCMonitor = new Map();
}

// ===========================
// OSC-Out: Sending Messages
// ===========================

/** @type {number} Minimum ms between sends per address (0 = no throttle) */
const OSC_THROTTLE_MS = 33;  // ~30 sends/sec max

/** @type {Map<string, {lastSent: number, pending: Object|null, timer: number|null}>} */
const throttleState = new Map();

/**
 * Send an OSC message via WebSocket
 * @param {Object} payload - OSC message payload
 * @param {Object} options - Send options
 *   - silent: don't log
 *   - immediate: bypass throttle (for events)
 */
export function sendOSC(payload, options = {}) {
  const { immediate = false } = options;

  if (!payload || typeof payload !== 'object') return;

  // Throttle continuous streams (rotation, scale, position values)
  // Events (button presses, state changes) should use immediate: true
  if (OSC_THROTTLE_MS > 0 && !immediate) {
    const key = payload.addr || payload.uid || 'default';
    const now = performance.now();
    let state = throttleState.get(key);

    if (!state) {
      state = { lastSent: 0, pending: null, timer: null };
      throttleState.set(key, state);
    }

    const elapsed = now - state.lastSent;

    if (elapsed < OSC_THROTTLE_MS) {
      // Too soon - store as pending and schedule send
      state.pending = payload;
      if (!state.timer) {
        state.timer = setTimeout(() => {
          state.timer = null;
          if (state.pending) {
            const p = state.pending;
            state.pending = null;
            state.lastSent = performance.now();
            _doSendOSC(p, options);
          }
        }, OSC_THROTTLE_MS - elapsed);
      }
      return;
    }

    state.lastSent = now;
    state.pending = null;
    if (state.timer) {
      clearTimeout(state.timer);
      state.timer = null;
    }
  }

  _doSendOSC(payload, options);
}

function _doSendOSC(payload, options = {}) {
  const { silent = false } = options;

  // Round float args to 4dp before they leave the client.
  // OSC floats are 32-bit (~7 sig digits); 4dp avoids noisy
  // wire values and logs while preserving all useful precision.
  if (Array.isArray(payload.args)) {
    payload.args = payload.args.map(v =>
      typeof v === 'number' && !Number.isInteger(v)
        ? Math.round(v * 10000) / 10000
        : v
    );
  }

  // Update OSC monitor map (for live console OSC monitor section)
  const monAddr = payload.addr || payload.uid || "unknown";
  let monArgs;
  if (Array.isArray(payload.args)) {
    monArgs = payload.args.slice();
  } else {
    // Build args from the numeric fields (osc_control, osc_value, etc.)
    monArgs = [];
    for (const [k, v] of Object.entries(payload)) {
      if (k === "type" || k === "addr" || k === "uid" || k === "timestamp") continue;
      if (typeof v === "number") monArgs.push(v);
    }
  }
  window.oscillaOSCMonitor.set(monAddr, {
    type: payload.type || "",
    addr: monAddr,
    args: monArgs,
    uid: payload.uid || "",
    lastUpdated: Date.now()
  });

  if (oscMuted) return;

  // Send via WebSocket
  if (!window.socket || window.socket.readyState !== WebSocket.OPEN) {
    if (!silent) console.warn("[OSC] Socket not ready", payload);
    return;
  }

  try {
    window.socket.send(JSON.stringify(payload));
  } catch (err) {
    console.warn("[OSC] Send failed", err, payload);
  }
}

// ===========================
// Mute Control
// ===========================

/**
 * Set this client's OSC mute state
 * @param {boolean} muted - Whether to mute OSC output
 */
export function setMuted(muted) {
  oscMuted = !!muted;
  reportOutputState();
}

/**
 * Tell the server whether this client's OSC output is on. The server forwards
 * OSC from the sync leader only — unless the leader is muted, in which case
 * it falls back to everyone (see server.js oscSourceAllows). Sent again on
 * every (re)connect (socket.js).
 */
export function reportOutputState() {
  const sock = window.socket;
  if (!sock || sock.readyState !== WebSocket.OPEN) return;
  try { sock.send(JSON.stringify({ type: "osc_output_state", enabled: !oscMuted })); } catch { /* ignore */ }
}
